import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { courseService, Course, PageResponse, CourseSearchRequest, CourseSearchFilters } from '../../services/courseService';
import { teacherService, Teacher } from '../../services/teacherService';
import { levelService, Level } from '../../services/levelService';
import { classeService, Classe } from '../../services/classeService';
import { useRights } from '../../hooks/useRights';
import { useHasAnyActionRight, courseActions } from '../../utils/actionRightsUtils';
import { useModal } from '../../context/ModalContext';
import { Pencil, Trash2 } from 'lucide-react';
import '../../styles/index.css';
import './Courses.css';
import TableActions from '../../components/TableActions';

const Courses: React.FC = () => {
  const { t } = useTranslation();
  const { hasRight } = useRights();
  const { showConfirm, showAlert } = useModal();
  const hasAnyActionRight = useHasAnyActionRight(courseActions());
  const [courses, setCourses] = useState<Course[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [classes, setClasses] = useState<Classe[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  const [isDeepSearch, setIsDeepSearch] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [filters, setFilters] = useState<CourseSearchFilters>({
    name: '',
    levelId: '',
    teacherId: '',
    enabled: undefined,
  });
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    levelId: '',
    teacherId: '',
    classIds: [] as string[],
    factor: 1.0,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    levelId: '',
    teacherId: '',
    classIds: [] as string[],
    factor: 1.0,
  });

  useEffect(() => {
    loadCourses(0, true);
    loadTeachers();
    loadLevels();
    loadClasses();
  }, []);

  const loadTeachers = async () => {
    try {
      const data = await teacherService.getAllTeachers();
      setTeachers(data);
    } catch (e: any) {
      console.error('Failed to load teachers:', e);
    }
  };

  const loadLevels = async () => {
    try {
      const data = await levelService.getAllLevels();
      setLevels(data);
    } catch (e: any) {
      console.error('Failed to load levels:', e);
    }
  };

  const loadClasses = async () => {
    try {
      const data = await classeService.getAllClasses();
      setClasses(data);
    } catch (e: any) {
      console.error('Failed to load classes:', e);
    }
  };

  const loadCourses = async (page: number = 0, reset: boolean = true) => {
    try {
      if (reset) {
        setLoading(true);
        setCurrentPage(0);
        setIsDeepSearch(false);
      } else {
        setLoadingMore(true);
      }

      const searchRequest: CourseSearchRequest = {
        page,
        size: 100,
        filters: isDeepSearch ? filters : undefined,
      };

      const response: PageResponse<Course> = await courseService.searchCourses(searchRequest);

      if (reset) {
        setCourses(response.content || []);
      } else {
        setCourses(prev => [...(prev || []), ...(response.content || [])]);
      }

      setCurrentPage(response.page);
      setHasMore(response.hasNext);
      setTotalElements(response.totalElements);
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load courses');
      console.error('Load courses error:', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreCourses = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      setLoadingMore(true);
      const searchFilters: CourseSearchFilters = {
        name: filters.name || undefined,
        levelId: filters.levelId || undefined,
        teacherId: filters.teacherId || undefined,
        enabled: filters.enabled !== undefined ? filters.enabled : undefined,
      };

      const searchRequest: CourseSearchRequest = {
        page: currentPage + 1,
        size: 100,
        filters: isDeepSearch ? searchFilters : undefined,
      };

      courseService.searchCourses(searchRequest).then(response => {
        setCourses(prev => [...(prev || []), ...(response.content || [])]);
        setCurrentPage(response.page);
        setHasMore(response.hasNext);
        setTotalElements(response.totalElements);
        setLoadingMore(false);
      }).catch(e => {
        setError(e.response?.data?.message || 'Failed to load more courses');
        setLoadingMore(false);
      });
    }
  }, [currentPage, hasMore, loadingMore, loading, isDeepSearch, filters]);

  const handleDeepSearch = async () => {
    try {
      setLoading(true);
      setIsDeepSearch(true);
      setCurrentPage(0);

      const searchFilters: CourseSearchFilters = {
        name: filters.name || undefined,
        levelId: filters.levelId || undefined,
        teacherId: filters.teacherId || undefined,
        enabled: filters.enabled !== undefined ? filters.enabled : undefined,
      };

      const searchRequest: CourseSearchRequest = {
        page: 0,
        size: 100,
        filters: searchFilters,
      };

      const response: PageResponse<Course> = await courseService.searchCourses(searchRequest);
      setCourses(response.content || []);
      setCurrentPage(response.page);
      setHasMore(response.hasNext);
      setTotalElements(response.totalElements);
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to search courses');
      console.error('Deep search error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    const handleScroll = () => {
      const container = currentTarget;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore && !loading) {
        loadMoreCourses();
      }
    };

    currentTarget.addEventListener('scroll', handleScroll);

    return () => {
      currentTarget.removeEventListener('scroll', handleScroll);
    };
  }, [loadMoreCourses, hasMore, loadingMore, loading]);

  const filteredCourses = isDeepSearch ? (courses || []) : (courses || []).filter((course) => {
    if (filters.name && !course.name?.toLowerCase().includes(filters.name.toLowerCase())) {
      return false;
    }
    if (filters.levelId && course.levelId !== filters.levelId) {
      return false;
    }
    if (filters.teacherId && course.teacherId !== filters.teacherId) {
      return false;
    }
    if (filters.enabled !== undefined && course.enabled !== filters.enabled) {
      return false;
    }
    return true;
  });

  const handleFilterChange = (key: keyof CourseSearchFilters, value: string | boolean | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setIsDeepSearch(false);
  };

  const clearFilters = () => {
    setFilters({
      name: '',
      levelId: '',
      teacherId: '',
      enabled: undefined,
    });
    setIsDeepSearch(false);
    loadCourses(0, true);
  };

  const handleEdit = (course: Course) => {
    setEditingCourse(course);
    setEditForm({
      name: course.name || '',
      description: course.description || '',
      levelId: course.levelId || '',
      teacherId: course.teacherId || '',
      classIds: course.classIds || [],
      factor: course.factor || 1.0,
    });
  };

  const handleCancelEdit = () => {
    setEditingCourse(null);
    setEditForm({
      name: '',
      description: '',
      levelId: '',
      teacherId: '',
      classIds: [],
      factor: 1.0,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingCourse) return;
    if (!editForm.name.trim()) {
      setError(t('courses.nameRequired'));
      return;
    }

    try {
      await courseService.updateCourse(editingCourse.id!, {
        name: editForm.name,
        description: editForm.description,
        levelId: editForm.levelId,
        teacherId: editForm.teacherId,
        classIds: editForm.classIds,
        factor: editForm.factor,
      });
      await loadCourses(0, true);
      handleCancelEdit();
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to update course');
      console.error('Update course error:', e);
    }
  };

  const handleDelete = (id: string) => {
    showConfirm(t('common.delete'), t('courses.deleteConfirm'), async () => {
      try {
        await courseService.deleteCourse(id);
        await loadCourses(0, true);
        setError('');
      } catch (e: any) {
        const errorMsg = e.response?.data || 'Failed to delete course';
        setError(errorMsg);
        showAlert(t('common.error'), errorMsg);
        console.error('Delete course error:', e);
      }
    });
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    setCreateForm({
      name: '',
      description: '',
      levelId: '',
      teacherId: '',
      classIds: [],
      factor: 1.0,
    });
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setCreateForm({
      name: '',
      description: '',
      levelId: '',
      teacherId: '',
      classIds: [],
      factor: 1.0,
    });
  };

  const handleSaveCreate = async () => {
    if (!createForm.name.trim()) {
      setError(t('courses.nameRequired'));
      return;
    }

    try {
      await courseService.createCourse({
        name: createForm.name,
        description: createForm.description,
        levelId: createForm.levelId,
        teacherId: createForm.teacherId,
        classIds: createForm.classIds,
        factor: createForm.factor,
      });
      await loadCourses(0, true);
      handleCancelCreate();
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to create course');
      console.error('Create course error:', e);
    }
  };

  const addClassId = (isEdit: boolean) => {
    const classIdSelect = isEdit ? document.getElementById('edit-class-select') as HTMLSelectElement : document.getElementById('create-class-select') as HTMLSelectElement;
    if (!classIdSelect || !classIdSelect.value) return;

    const classId = classIdSelect.value;
    if (isEdit) {
      if (!editForm.classIds.includes(classId)) {
        setEditForm({
          ...editForm,
          classIds: [...editForm.classIds, classId],
        });
      }
    } else {
      if (!createForm.classIds.includes(classId)) {
        setCreateForm({
          ...createForm,
          classIds: [...createForm.classIds, classId],
        });
      }
    }
    classIdSelect.value = '';
  };

  const removeClassId = (classId: string, isEdit: boolean) => {
    if (isEdit) {
      setEditForm({
        ...editForm,
        classIds: editForm.classIds.filter((id) => id !== classId),
      });
    } else {
      setCreateForm({
        ...createForm,
        classIds: createForm.classIds.filter((id) => id !== classId),
      });
    }
  };

  const getTeacherName = (teacherId?: string) => {
    if (!teacherId) return '-';
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? `${teacher.firstName} ${teacher.lastName}` : teacherId;
  };

  const getLevelName = (levelId?: string) => {
    if (!levelId) return '-';
    const level = levels.find(l => l.id === levelId);
    return level ? level.name : levelId;
  };

  const getClassName = (classId: string) => {
    const classe = classes.find(c => c.id === classId);
    return classe ? classe.name : classId;
  };

  if (loading && courses.length === 0) {
    return <div className="bo-container">{t('common.loading')}</div>;
  }

  return (
    <div className="bo-container">
      <div className="bo-header">
        <div className="header-left">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="btn-toggle-sidebar"
            title={sidebarOpen ? t('common.hideFilters') : t('common.showFilters')}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
          <h2>{t('courses.title')}</h2>
        </div>
        {hasRight('courses:c') && (
          <button className="btn-create" onClick={handleStartCreate}>
            {t('courses.createNew')}
          </button>
        )}
      </div>

      <div className="bo-content-wrapper">
        {sidebarOpen && (
          <div className="bo-sidebar">
            <div className="sidebar-header">
              <h3>{t('courses.filters')}</h3>
              <div className="sidebar-header-actions">
                <button onClick={handleDeepSearch} className="btn-deep-search">
                  {t('courses.deepSearch')}
                </button>
                <button onClick={clearFilters} className="btn-clear-filters">
                  {t('common.clear')}
                </button>
              </div>
            </div>
            {isDeepSearch && (
              <div className="deep-search-indicator">
                <small>{t('courses.deepSearchActive')} ({totalElements} {t('courses.results')})</small>
              </div>
            )}
            <div className="sidebar-body">
              <div className="filter-group">
                <label>{t('courses.name')}</label>
                <input
                  type="text"
                  value={filters.name || ''}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                  placeholder={t('courses.filterByName')}
                />
              </div>
              <div className="filter-group">
                <label>{t('courses.levelId')}</label>
                <select
                  value={filters.levelId || ''}
                  onChange={(e) => handleFilterChange('levelId', e.target.value)}
                >
                  <option value="">{t('courses.allLevels')}</option>
                  {levels.map((level) => (
                    <option key={level.id} value={level.id}>
                      {level.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>{t('courses.teacherId')}</label>
                <select
                  value={filters.teacherId || ''}
                  onChange={(e) => handleFilterChange('teacherId', e.target.value)}
                >
                  <option value="">{t('courses.allTeachers')}</option>
                  {teachers.map((teacher) => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.firstName} {teacher.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>{t('courses.enabled')}</label>
                <select
                  value={filters.enabled === true ? 'true' : filters.enabled === false ? 'false' : ''}
                  onChange={(e) => handleFilterChange('enabled', e.target.value === 'true' ? true : e.target.value === 'false' ? false : undefined)}
                >
                  <option value="">{t('courses.all')}</option>
                  <option value="true">{t('common.yes')}</option>
                  <option value="false">{t('common.no')}</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div className={`bo-main-content ${!sidebarOpen ? 'full-width' : ''}`}>
          {error && <div className="error-message">{error}</div>}

          {isCreating && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>{t('courses.createNew')}</h3>
                <div className="form-group">
                  <div className="form-field">
                    <label>{t('courses.name')} *</label>
                    <input
                      type="text"
                      value={createForm.name}
                      onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                      placeholder={t('courses.namePlaceholder')}
                    />
                  </div>
                  <div className="form-field">
                    <label>{t('courses.description')}</label>
                    <textarea
                      value={createForm.description}
                      onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                      placeholder={t('courses.descriptionPlaceholder')}
                      rows={3}
                    />
                  </div>
                  <div className="form-field">
                    <label>{t('courses.levelId')}</label>
                    <select
                      value={createForm.levelId}
                      onChange={(e) => setCreateForm({ ...createForm, levelId: e.target.value })}
                    >
                      <option value="">{t('courses.levelIdPlaceholder')}</option>
                      {levels.map((level) => (
                        <option key={level.id} value={level.id}>
                          {level.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>{t('courses.teacherId')}</label>
                    <select
                      value={createForm.teacherId}
                      onChange={(e) => setCreateForm({ ...createForm, teacherId: e.target.value })}
                    >
                      <option value="">{t('courses.teacherIdPlaceholder')}</option>
                      {teachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.firstName} {teacher.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>{t('courses.factor')} *</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={createForm.factor}
                      onChange={(e) => setCreateForm({ ...createForm, factor: parseFloat(e.target.value) || 0 })}
                      placeholder={t('courses.factorPlaceholder')}
                    />
                  </div>
                  <div className="form-field">
                    <label>{t('courses.classIds')}</label>
                    <div className="input-group">
                      <select id="create-class-select">
                        <option value="">{t('courses.addClassIdPlaceholder')}</option>
                        {classes.map((classe) => (
                          <option key={classe.id} value={classe.id}>
                            {classe.name}
                          </option>
                        ))}
                      </select>
                      <button type="button" onClick={() => addClassId(false)} className="btn-add">
                        {t('common.add')}
                      </button>
                    </div>
                    {createForm.classIds.length > 0 && (
                      <div className="tags-list">
                        {createForm.classIds.map((classId, index) => (
                          <span key={index} className="tag">
                            {getClassName(classId)}
                            <button
                              type="button"
                              onClick={() => removeClassId(classId, false)}
                              className="btn-remove-tag"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn-cancel" onClick={handleCancelCreate}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn-save" onClick={handleSaveCreate}>
                    {t('common.save')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {editingCourse && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>{t('courses.edit')}</h3>
                <div className="form-group">
                  <div className="form-field">
                    <label>{t('courses.name')} *</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label>{t('courses.description')}</label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div className="form-field">
                    <label>{t('courses.levelId')}</label>
                    <select
                      value={editForm.levelId}
                      onChange={(e) => setEditForm({ ...editForm, levelId: e.target.value })}
                    >
                      <option value="">{t('courses.levelIdPlaceholder')}</option>
                      {levels.map((level) => (
                        <option key={level.id} value={level.id}>
                          {level.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>{t('courses.teacherId')}</label>
                    <select
                      value={editForm.teacherId}
                      onChange={(e) => setEditForm({ ...editForm, teacherId: e.target.value })}
                    >
                      <option value="">{t('courses.teacherIdPlaceholder')}</option>
                      {teachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.firstName} {teacher.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>{t('courses.factor')} *</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={editForm.factor}
                      onChange={(e) => setEditForm({ ...editForm, factor: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="form-field">
                    <label>{t('courses.classIds')}</label>
                    <div className="input-group">
                      <select id="edit-class-select">
                        <option value="">{t('courses.addClassIdPlaceholder')}</option>
                        {classes.map((classe) => (
                          <option key={classe.id} value={classe.id}>
                            {classe.name}
                          </option>
                        ))}
                      </select>
                      <button type="button" onClick={() => addClassId(true)} className="btn-add">
                        {t('common.add')}
                      </button>
                    </div>
                    {editForm.classIds.length > 0 && (
                      <div className="tags-list">
                        {editForm.classIds.map((classId, index) => (
                          <span key={index} className="tag">
                            {getClassName(classId)}
                            <button
                              type="button"
                              onClick={() => removeClassId(classId, true)}
                              className="btn-remove-tag"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn-cancel" onClick={handleCancelEdit}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn-save" onClick={handleSaveEdit}>
                    {t('common.save')}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bo-table-container">
            {filteredCourses.length === 0 && !loading ? (
              <div className="no-data-message">
                <p>{t('courses.noCoursesFound')}</p>
              </div>
            ) : (
              <>
                <div className="bo-table-wrapper" ref={observerTarget}>
                  <table className="bo-table">
                    <thead>
                      <tr>
                        <th>{t('courses.name')}</th>
                        <th>{t('courses.description')}</th>
                        <th>{t('courses.levelId')}</th>
                        <th>{t('courses.teacherId')}</th>
                        <th>{t('courses.classIds')}</th>
                        {hasAnyActionRight && <th>{t('common.actions')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCourses.map((course) => (
                        <tr key={course.id}>
                          <td>{course.name}</td>
                          <td>{course.description || '-'}</td>
                          <td>{getLevelName(course.levelId)}</td>
                          <td>{getTeacherName(course.teacherId)}</td>
                          <td>
                            {course.classIds && course.classIds.length > 0 ? (
                              <div className="tags-display">
                                {course.classIds.map((classId, index) => (
                                  <span key={index} className="tag-small">{getClassName(classId)}</span>
                                ))}
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                          {hasAnyActionRight && (
                            <td>
                              <TableActions actions={[
                                ...(hasRight('courses:u') ? [{
                                  label: t('common.edit'),
                                  onClick: () => handleEdit(course),
                                  className: 'btn-edit',
                                  icon: <Pencil size={16} />,
                                  hideLabelDesktop: true
                                }] : []),
                                ...(hasRight('courses:d') ? [{
                                  label: t('common.delete'),
                                  onClick: () => handleDelete(course.id!),
                                  className: 'btn-delete',
                                  icon: <Trash2 size={16} />,
                                  hideLabelDesktop: true,
                                  variant: 'danger' as const
                                }] : [])
                              ]} />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="scroll-trigger">
                    {loadingMore && (
                      <div className="loading-more">
                        <small>{t('common.loading')}...</small>
                      </div>
                    )}
                    {!hasMore && courses.length > 0 && (
                      <div className="no-more-data">
                        <small>{t('courses.allLoaded')}</small>
                      </div>
                    )}
                  </div>
                </div>
                {!isDeepSearch && (
                  <div className="table-info">
                    <small>{t('courses.showingLoaded')} ({courses.length} {t('courses.loaded')})</small>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Courses;
